/**
 * Who owns the band below the pill bar — the strip of viewport every {@link ./CardSheet} occupies.
 *
 * The card sheet is a `position: fixed` surface at one place on screen, so two open at once do not
 * tile: they cover each other, install competing Escape listeners and race their focus restoration.
 * ui#1061 settled that between the sender's two editors with ONE editor identity in `TransferPanel`.
 * The rule is the same, but the surfaces are no longer siblings: the destination sheet is route-driven
 * from the App shell, the two editors belong to the sender panel, and the Personalization sheet is
 * raised from the top bar, which stays live above every one of them. No component contains all three,
 * so the "one at a time" state cannot live in any of them.
 *
 * It lives here instead: a plain module, no React (CLAUDE.md — rules outside the framework), holding
 * the one thing that has to be shared. Each open sheet CLAIMS the band and hands over how to close
 * itself; a later claim evicts the current holder through exactly that callback, so eviction closes a
 * sheet the way its own ✕ does — with its exit transition, its `onExited`, and its owner's state left
 * consistent. The primitive claims on behalf of every caller, so no sheet has to remember to.
 *
 * Process-wide by design: it is one screen. Nothing here touches the DOM, so an SSG prerender — which
 * runs no effects and therefore never claims — is unaffected.
 */

/** A card sheet that can be asked to close because another surface has taken the band. */
export interface SheetBandClaimant {
	/**
	 * Close this sheet: called when someone else claims the band, at most once per claim, and never on
	 * the claimant that is doing the claiming.
	 */
	release: () => void;
}

/** The sheet currently owning the band, or `null` while no card sheet is open. */
let holder: SheetBandClaimant | null = null;

/**
 * Takes the band for `claimant`, closing whoever held it.
 *
 * The holder is swapped BEFORE the eviction runs, so the evicted sheet's own teardown — which ends in
 * {@link releaseSheetBand} once its exit transition finishes — finds it no longer holds anything and
 * leaves the new holder's claim intact.
 */
export function claimSheetBand(claimant: SheetBandClaimant): void {
	if (holder === claimant) {
		return;
	}
	const evicted = holder;
	holder = claimant;
	evicted?.release();
}

/**
 * Gives the band up, but only if `claimant` still holds it.
 *
 * The ownership check is the load-bearing half: an evicted sheet releases on its way out, after the
 * surface that evicted it has already claimed. Clearing unconditionally would hand the band back to
 * nobody while a sheet is open on screen, and the next claim would then evict nothing.
 */
export function releaseSheetBand(claimant: SheetBandClaimant): void {
	if (holder === claimant) {
		holder = null;
	}
}
